import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons'
import { useCallback, useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import { Button } from '@/components'
import { Modal } from '@/components/Modal/Modal'
import { CheckBoxFormField } from '@/components/UberForm'
import { StructureDto } from '@/endpoints/models'
import {
	useAppContext,
	useAppDispatch,
	useAppStore,
	useAuthContext,
} from '@/hooks'
import { useBulkUnlockMutation } from '@/rtkApi/mddApiV2'
import { loadNodeChildren } from '@/store/modules/node/actions'
import { logError } from '@/utils/errorHandling'

import { useSelectedBranchNumericId, useTreePanelOperations } from '../TreePanelContext'

type Props = {
	allowSkip?: boolean
	context?: 'standalone' | 'batchEdit'
	onClose: () => void
	onSkip?: (unlockedNodeIds: number[]) => void
	onUnlockSuccess?: () => void
	structures?: StructureDto[]
}

export const BatchUnlockModal = ({
	onClose,
	structures,
	onUnlockSuccess,
	allowSkip = false,
	onSkip,
	context = 'standalone',
}: Props) => {
	const { t } = useAppContext()
	const dispatch = useAppDispatch()
	const auth = useAuthContext()
	const [unlocking, setUnlocking] = useState(false)
	const [forceUnlock, setForceUnlock] = useState(false)

	const loggedUserId = useAppStore((state) => state.auth.user?.id)
	const { nodes, selectedNodes } = useAppStore((state) => state.node)

	const [bulkUnlock] = useBulkUnlockMutation()
	const branchId = useSelectedBranchNumericId()
	const { refreshSystemsSilent } = useTreePanelOperations()

	const canForceUnlock = useMemo(() => auth.canForceUnlock(), [auth])

	const selectedStructures = useMemo(
		() => structures ?? selectedNodes.map((id) => nodes[id]).filter(Boolean),
		[nodes, selectedNodes, structures],
	)

	const lockedNodes = useMemo(
		() => selectedStructures.filter((node) => node?.lockTime),
		[selectedStructures],
	)

	const lockedByOthers = useMemo(
		() =>
			selectedStructures.filter(
				(node) => node?.lockTime && node?.lockUserId !== loggedUserId,
			),
		[loggedUserId, selectedStructures],
	)

	const lockedByMe = useMemo(
		() =>
			selectedStructures.filter(
				(node) => node?.lockTime && node?.lockUserId === loggedUserId,
			),
		[loggedUserId, selectedStructures],
	)

	const nodesToUnlock = useMemo(() => {
		// In batch edit context with force unlock permission, unlock all locked nodes
		if (context === 'batchEdit' && canForceUnlock) {
			return lockedNodes
		}
		if (canForceUnlock && forceUnlock) {
			return lockedNodes
		}
		return lockedByMe
	}, [canForceUnlock, context, forceUnlock, lockedByMe, lockedNodes])

	const updateUnlockedNodes = useCallback(
		async () => {
			refreshSystemsSilent().catch((e) =>
				logError(e, { operation: 'BatchUnlockModal.refreshSystemsSilent' }),
			)
			const parentIds = new Set<number>()
			nodesToUnlock.forEach((node) => {
				if (node?.parentStructureId) {
					parentIds.add(node.parentStructureId)
				}
			})
			for (const parentId of parentIds) {
				dispatch(loadNodeChildren(parentId))
			}
		},
		[dispatch, refreshSystemsSilent, nodesToUnlock],
	)

	const handleUnlock = useCallback(async () => {
		setUnlocking(true)

		const nodeIds = nodesToUnlock
			.map((node) => node?.id)
			.filter(Boolean) as number[]

		if (nodeIds.length > 0 && branchId != null) {
			try {
				const shouldForceUnlock =
					canForceUnlock && (forceUnlock || context === 'batchEdit')
				await bulkUnlock({
					branchId,
					id: nodeIds,
					force: shouldForceUnlock,
				}).unwrap()
				await updateUnlockedNodes()
				setUnlocking(false)
				onUnlockSuccess?.()
				// In batch edit context, don't close - let parent handle transition to edit modal
				if (context !== 'batchEdit') {
					onClose()
				}
				return
			} catch (error) {
				logError(error, { operation: 'BatchUnlockModal.handleUnlock' })
				toast.error(t('UNLOCK_FAILED'))
				setUnlocking(false)
				onClose()
				return
			}
		}

		setUnlocking(false)
		onClose()
	}, [
		branchId,
		bulkUnlock,
		canForceUnlock,
		context,
		forceUnlock,
		nodesToUnlock,
		onClose,
		onUnlockSuccess,
		t,
		updateUnlockedNodes,
	])

	const handleSkip = useCallback(() => {
		const unlockedNodeIds = selectedStructures
			.filter((node) => !node?.lockTime || node?.lockUserId === loggedUserId)
			.map((node) => node?.id)
			.filter(Boolean) as number[]

		onSkip?.(unlockedNodeIds)
	}, [loggedUserId, onSkip, selectedStructures])

	const getHeader = () => {
		if (context === 'batchEdit') {
			return t('WARNING_LOCKED_ELEMENTS_BY_OTHER_USERS')
		}
		return t('BATCH_UNLOCK_HEADER')
	}

	const showSkipButton =
		allowSkip && !canForceUnlock && lockedByOthers.length > 0

	const showForceUnlockCheckbox =
		context === 'standalone' && canForceUnlock && lockedByOthers.length > 0

	return (
		<Modal
			open={true}
			contentStyle={{ width: '500px' }}
			header={getHeader()}
			footer={() => (
				<>
					{context === 'batchEdit' && canForceUnlock ? (
						<Button
							icon={faCheck}
							onClick={handleUnlock}
							isLoading={unlocking}
							schema="warn"
							disabled={lockedNodes.length === 0}
						>
							{t('UNLOCK')}
						</Button>
					) : nodesToUnlock.length > 0 ? (
						<Button
							icon={faCheck}
							onClick={handleUnlock}
							isLoading={unlocking}
							schema="warn"
						>
							{t('UNLOCK')} ({nodesToUnlock.length})
						</Button>
					) : showSkipButton ? (
						<Button icon={faCheck} onClick={handleSkip} schema="warn">
							{t('PROCEED')}
						</Button>
					) : null}
					<Button schema="transparent" onClick={onClose} icon={faTimes}>
						{t('CANCEL')}
					</Button>
				</>
			)}
			onClose={onClose}
		>
			<div className="flex flex-col gap-4">
				{lockedNodes.length === 0 ? (
					<p>{t('NO_LOCKED_ELEMENTS_SELECTED')}</p>
				) : context === 'batchEdit' ? (
					<p>
						{canForceUnlock
							? t('SOME_SELECTED_ELEMENTS_ARE_LOCKED_BY_OTHER_USERS_PROCEED')
							: t('SOME_SELECTED_ELEMENTS_ARE_LOCKED_BY_OTHER_USERS_SKIP')}
					</p>
				) : (
					<>
						<div className="flex flex-col gap-2">
							<p>
								{t('LOCKED_BY_YOU')}: <strong>{lockedByMe.length}</strong>
							</p>
							{lockedByOthers.length > 0 && (
								<p>
									{t('LOCKED_BY_OTHERS')}:{' '}
									<strong>{lockedByOthers.length}</strong>
								</p>
							)}
						</div>

						{showForceUnlockCheckbox && (
							<CheckBoxFormField
								name="forceUnlock"
								title={t('FORCE_UNLOCK_OTHERS')}
								value={forceUnlock}
								onChange={(checked) => setForceUnlock(checked as boolean)}
							/>
						)}

						{!canForceUnlock &&
							lockedByOthers.length > 0 &&
							lockedByMe.length === 0 && (
								<p className="text-red-500">
									{t('BATCH_UNLOCK_NO_PERMISSION')}
								</p>
							)}
					</>
				)}
			</div>
		</Modal>
	)
}
